import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  LucideArrowLeft,
  LucideEye,
  LucideEyeOff,
  LucideDownload,
  LucideChevronLeft,
  LucideChevronRight,
  LucideCheck,
  LucideLayoutGrid,
  LucideUsers,
  LucideSettings2,
} from "lucide-react";
import { PDFDocument } from "pdf-lib";
import { fill as fillPDFProvincial, SCHEMA_TO_PDF_FIELD, PAGE_INDEX_FOR_SCHEMA_FIELD, STEP_ANCHOR_FIELDS } from "./fill.js";
import templateUrl from "./template.pdf";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();
import { BACKEND_URL } from "@/constants/api";
import { PRESETS } from "./presets.js";
import { formSchema, defaultValues } from "./schema.js";
import {
  selectItems,
  twoPlans,
  threePlans,
  fourPlans,
  frenchPlans,
  ersCompleted as ersCompletedOpts,
  ersBenchmark as ersBenchmarkOpts,
} from "./options.js";
import { STEPS, STEP_FIELDS, SUBJECT_STEP_IDS, TOP_STEPS, SUB_STEPS, ARTS_ROWS } from "./steps.js";
import { LSWH_COLOR_MAP } from "../shared/colorSystem";
import { PillSelector } from "../shared/PillSelector";
import { PDFPreview } from "../shared/PDFPreview";
import { SectionHeading } from "../shared/SectionHeading";
import { AIGeneratedTextarea } from "../shared/AIGeneratedTextarea";
import { FormFieldGrid } from "../shared/FormFieldGrid";

// ─── Reusable primitives ──────────────────────────────────────────────────────

// Compact grade box pair - renders two side-by-side grade inputs
function GradePair({ control, field1, field2, label1 = "Term 1", label2 = "Term 2" }) {
  return (
    <div className="flex items-end gap-3">
      <FormField
        control={control}
        name={field1}
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label1}</span>
            <FormControl>
              <input
                {...field}
                maxLength={2}
                className="w-12 h-11 text-center text-lg font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
              />
            </FormControl>
          </FormItem>
        )}
      />
      <div className="w-4 h-px bg-slate-200 mb-5" />
      <FormField
        control={control}
        name={field2}
        render={({ field }) => (
          <FormItem className="flex flex-col items-center">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label2}</span>
            <FormControl>
              <input
                {...field}
                maxLength={2}
                className="w-12 h-11 text-center text-lg font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}

// Standard subject card: plan pills + compact grade pair + comments
function SubjectCard({ title, planField, grade1Field, grade2Field, commentsField, plans, nameField, control }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Subject header stripe */}
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {nameField && (
          <FormField
            control={control}
            name={nameField}
            render={({ field }) => (
              <FormItem className="mb-0">
                <FormControl>
                  <input
                    {...field}
                    placeholder="Subject name"
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white focus:border-teal-400 focus:outline-none w-40"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}
      </div>

      <div className="p-5 space-y-4">
        {/* Plan */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
          <FormField
            control={control}
            name={planField}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PillSelector size="sm" value={field.value} onChange={field.onChange} options={plans} colorMap={{}} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        {/* Grades */}
        {grade1Field && grade2Field && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades</p>
            <GradePair control={control} field1={grade1Field} field2={grade2Field} />
          </div>
        )}

        {/* Comments */}
        {commentsField && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Comments</p>
            <FormField
              control={control}
              name={commentsField}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Teacher comments…" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Two-level step indicator: top row (6 nodes) + persistent subject sub-row
function StepIndicator({ currentStep, completedSteps, onStepClick }) {
  const isSubjectStep = SUBJECT_STEP_IDS.has(currentStep);

  return (
    <div>
      {/* ── Top row ───────────────────────────────────────────────────────── */}
      <div className="flex items-start w-full">
        {TOP_STEPS.map((step, index) => {
          const isSubjectsNode = step.id === "subjects";
          const isLast = index === TOP_STEPS.length - 1;
          const isDone = isSubjectsNode
            ? step.stepIds.every((id) => completedSteps.has(id))
            : completedSteps.has(step.id);
          const isCurrent = isSubjectsNode ? isSubjectStep : step.id === currentStep;

          const handleClick = () =>
            isSubjectsNode ? onStepClick(step.jumpTo) : onStepClick(step.id);

          return (
            <div key={String(step.id)} className="flex items-center" style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}>
              <div className="flex flex-col items-center" style={{ minWidth: "28px" }}>
                <button
                  type="button"
                  onClick={handleClick}
                  title={isSubjectsNode ? "Subjects" : String(step.label)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 transition-all cursor-pointer shrink-0 ${
                    isDone && !isCurrent
                      ? "bg-teal-700 border-teal-700 text-white"
                      : isCurrent
                      ? "bg-teal-600 border-teal-600 text-white scale-110 ring-4 ring-inset ring-teal-400/50"
                      : "bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200"
                  }`}
                >
                  {isDone && !isCurrent ? (
                    <LucideCheck size={11} />
                  ) : isSubjectsNode ? (
                    <LucideLayoutGrid size={12} />
                  ) : (
                    step.id
                  )}
                </button>
                <span
                  className={`text-[9px] mt-0.5 text-center leading-tight ${
                    isCurrent ? "text-teal-700 font-bold" : isDone ? "text-teal-500 font-medium" : "text-slate-400 font-medium"
                  }`}
                  style={{ maxWidth: "44px", wordBreak: "break-word" }}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div className={`h-px flex-1 mx-0.5 mb-4 ${isDone ? "bg-teal-500" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* ── Subject sub-row ───────────────────────────────────────────────── */}
      <div className="mt-2 mx-1 rounded-xl bg-slate-50/80 border border-slate-100 px-4 py-2">
        <div className="flex items-start w-full">
          {SUB_STEPS.map((step, index) => {
            const isDone = completedSteps.has(step.id);
            const isCurrent = step.id === currentStep;
            const isLast = index === SUB_STEPS.length - 1;
            return (
              <div key={step.id} className="flex items-center" style={{ flex: isLast ? "0 0 auto" : "1 1 0" }}>
                <div className="flex flex-col items-center" style={{ minWidth: "22px" }}>
                  <button
                    type="button"
                    onClick={() => onStepClick(step.id)}
                    title={step.label}
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold border-[1.5px] transition-all cursor-pointer shrink-0 ${
                      isDone && !isCurrent
                        ? "bg-teal-500 border-teal-500 text-white"
                        : isCurrent
                        ? "bg-teal-500 border-teal-500 text-white scale-125 ring-2 ring-inset ring-teal-300/50"
                        : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                    }`}
                  >
                    {isDone && !isCurrent ? <LucideCheck size={8} /> : step.id}
                  </button>
                  <span
                    className={`text-[8px] mt-0.5 text-center leading-tight ${
                      isCurrent ? "text-teal-600 font-bold" : isDone ? "text-teal-400 font-medium" : "text-slate-400 font-medium"
                    }`}
                    style={{ maxWidth: "44px", wordBreak: "break-word" }}
                  >
                    {step.label}
                  </span>
                </div>
                {!isLast && (
                  <div className={`h-px flex-1 mx-0.5 mb-3.5 ${isDone ? "bg-teal-300" : "bg-slate-200"}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export const FreeTrialReportCardProvincialGr16202309 = ({ reportId, backUrl, contextInfo, defaultsGroupId, onFormChange, onRequestSave } = {}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [filledPDF, setFilledPDF] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState({ lswh: false });
  const [aiError, setAiError] = useState(null);

  const getInitialPage = (step) => step <= 3 ? 1 : step <= 7 ? 2 : 3;
  const [scrollTargets, setScrollTargets] = useState(
    Object.fromEntries(STEPS.map((s) => [s.id, { page: getInitialPage(s.id), topFraction: 0 }]))
  );

  const debounceRef = useRef(null);
  const regenDebounceRef = useRef(null);
  const fieldPositionsRef = useRef({});
  const lastChangedFieldRef = useRef(null);
  const formPaneRef = useRef(null);
  const { getToken } = useAuth();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Restore saved data and generate initial PDF
  useEffect(() => {
    let initialValues;
    if (reportId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = json.data && Object.keys(json.data).length > 0 ? json.data : form.getValues();
            form.reset(values);
            fillPDFProvincial(values).then((url) => setFilledPDF(url));
          })
          .catch(() => fillPDFProvincial(form.getValues()).then((url) => setFilledPDF(url)))
      );
      return;
    }
    if (defaultsGroupId) {
      getToken().then((token) =>
        fetch(`${BACKEND_URL}api/report-groups/${defaultsGroupId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
          .then((r) => r.json())
          .then((json) => {
            const values = { ...form.getValues(), ...(json.reportDefaults || {}) };
            form.reset(values);
            fillPDFProvincial(values).then((url) => setFilledPDF(url));
          })
          .catch(() => fillPDFProvincial(form.getValues()).then((url) => setFilledPDF(url)))
      );
      return;
    }
    const saved = localStorage.getItem("formData-provincial-gr1-6-2023-09-v1");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        form.reset(parsed);
        initialValues = parsed;
      } catch {
        initialValues = form.getValues();
      }
    } else {
      initialValues = form.getValues();
    }
    fillPDFProvincial(initialValues).then((url) => setFilledPDF(url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build per-step scroll targets + per-field bounding boxes for yellow highlight
  useEffect(() => {
    async function buildTargets() {
      try {
        const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
        const pdfDoc = await PDFDocument.load(bytes);
        const pdfForm = pdfDoc.getForm();
        const pages = pdfDoc.getPages();

        const updated = {};
        for (const [stepStr, fieldName] of Object.entries(STEP_ANCHOR_FIELDS)) {
          const step = Number(stepStr);
          const pageIdx = step <= 3 ? 0 : step <= 7 ? 1 : 2;
          try {
            const f = pdfForm.getField(fieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) throw new Error("no widgets");
            const rect = widgets[0].getRectangle();
            const pageH = pages[pageIdx].getHeight();
            const topFromTop = Math.max(0, pageH - rect.y - rect.height - 40);
            updated[step] = { page: pageIdx + 1, topFraction: Math.min(0.95, topFromTop / pageH) };
          } catch {
            updated[step] = { page: pageIdx + 1, topFraction: 0 };
          }
        }
        setScrollTargets((prev) => ({ ...prev, ...updated }));

        const positions = {};
        for (const [schemaField, pdfFieldName] of Object.entries(SCHEMA_TO_PDF_FIELD)) {
          const pageIdx = PAGE_INDEX_FOR_SCHEMA_FIELD[schemaField] ?? 0;
          try {
            const f = pdfForm.getField(pdfFieldName);
            const widgets = f.acroField.getWidgets();
            if (!widgets.length) continue;
            const rect = widgets[0].getRectangle();
            positions[schemaField] = { pageIndex: pageIdx, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
          } catch { /* field not in template */ }
        }
        fieldPositionsRef.current = positions;
      } catch (e) {
        console.error("Failed to build PDF scroll targets:", e);
      }
    }
    buildTargets();
  }, []);

  // Auto-save (300 ms) + live PDF regeneration with highlight (500 ms)
  useEffect(() => {
    const subscription = form.watch((_, { name }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (reportId) {
          getToken().then((token) =>
            fetch(`${BACKEND_URL}api/reports/${reportId}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ data: form.getValues() }),
            })
          );
        } else if (defaultsGroupId) {
          onFormChange?.(form.getValues());
        } else {
          localStorage.setItem("formData-provincial-gr1-6-2023-09-v1", JSON.stringify(form.getValues()));
        }
      }, 300);

      if (name) lastChangedFieldRef.current = name;

      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
      setIsPdfGenerating(true);
      regenDebounceRef.current = setTimeout(async () => {
        const changedField = lastChangedFieldRef.current;
        const highlightRect = changedField ? (fieldPositionsRef.current[changedField] ?? null) : null;
        const url = await fillPDFProvincial(form.getValues(), highlightRect);
        setFilledPDF(url);
        setIsPdfGenerating(false);
      }, 500);
    });
    return () => {
      subscription.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (regenDebounceRef.current) clearTimeout(regenDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const handleDownload = async () => {
    if (!filledPDF) return;
    const cleanUrl = await fillPDFProvincial(form.getValues(), null);
    const { studentName, oen } = form.getValues();
    const fileName = [studentName, oen].filter(Boolean).join("_").replace(/\s+/g, " ").trim() || "provincial-report-card";
    const a = document.createElement("a");
    a.href = cleanUrl;
    a.setAttribute("download", `${fileName}.pdf`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleNext = () => {
    setCurrentStep((s) => Math.min(s + 1, STEPS.length));
    formPaneRef.current?.scrollTo({ top: 0 });
  };
  const handlePrev = () => {
    setCurrentStep((s) => Math.max(s - 1, 1));
    formPaneRef.current?.scrollTo({ top: 0 });
  };

  const toggleManualComplete = (stepId) => {
    const stepFields = STEP_FIELDS[stepId];
    const current = new Set(form.getValues('_completedFields') || []);
    const isComplete = stepFields.every((f) => current.has(f));
    if (isComplete) {
      stepFields.forEach((f) => current.delete(f));
    } else {
      stepFields.forEach((f) => current.add(f));
    }
    form.setValue('_completedFields', [...current]);
  };

  const handleGenerateAI = async () => {
    setAiError(null);
    const token = await getToken();
    if (!token) { setAiError("Sign in to use AI comment generation."); return; }
    setIsGeneratingAI((p) => ({ ...p, lswh: true }));
    try {
      const v = form.getValues();
      const res = await fetch(`${BACKEND_URL}api/report-comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reportId: reportId || "free-trial",
          strengths: v.strengthsNextStepsLSWH || "",
          nextSteps: v.strengthsNextStepsLSWH || "",
          studentInfo: {
            grade: v.grade, absent: v.daysAbsent, late: v.timesLate,
            responsibility: v.responsibility1, organized: v.organization1,
            independent: v.independentWork1, collaboration: v.collaboration1,
            initiative: v.initiative1,
          },
        }),
      });
      if (res.status === 403) { setAiError("AI generation quota exceeded. Please upgrade your plan."); return; }
      if (!res.ok) { setAiError("Failed to generate AI comment. Please try again."); return; }
      const { comment } = await res.json();
      form.setValue("strengthsNextStepsLSWH", comment);
    } catch {
      setAiError("Failed to generate AI comment. Please try again.");
    } finally {
      setIsGeneratingAI((p) => ({ ...p, lswh: false }));
    }
  };

  const watchedValues = form.watch();
  const completedFields = new Set(watchedValues._completedFields || []);
  const completedSteps = new Set(
    STEPS.filter((step) =>
      STEP_FIELDS[step.id].every((f) => completedFields.has(f))
    ).map((s) => s.id)
  );

  // ── Step content renderer ────────────────────────────────────────────────────
  const renderStep = () => {
    const { control } = form;

    // ── Step 1: Student Info ──────────────────────────────────────────────────
    if (currentStep === 1) {
      return (
        <div className="space-y-5">
          <SectionHeading title="Student Information" />
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: "studentName",      label: "Student Name",       colSpan: 2 },
                { name: "oen",              label: "OEN"                            },
                { name: "date",             label: "Date"                           },
                { name: "grade",            label: "Grade",              small: true },
                { name: "gradeInSeptember", label: "Grade in Sept.",     small: true },
                { name: "teacher",          label: "Teacher",            colSpan: 2 },
              ].map(({ name, label, colSpan, small }) => (
                <FormField
                  key={name}
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <FormItem className={colSpan === 2 ? "col-span-2" : ""}>
                      <FormLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {label}
                      </FormLabel>
                      <FormControl>
                        {small ? (
                          <input
                            {...field}
                            maxLength={3}
                            className="w-16 h-10 text-center text-base font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors block"
                          />
                        ) : (
                          <Input {...field} />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      );
    }

    // ── Step 2: School & Attendance ───────────────────────────────────────────
    if (currentStep === 2) {
      return (
        <div className="space-y-5">
          <SectionHeading title="School & Attendance" />

          {/* School info */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">School Details</p>
            <FormFieldGrid
              fields={[
                { name: "board",     label: "Board",          colSpan: 2 },
                { name: "school",    label: "School",         colSpan: 2 },
                { name: "address1",  label: "Address Line 1", colSpan: 2 },
                { name: "address2",  label: "Address Line 2", colSpan: 2 },
                { name: "principal", label: "Principal"                   },
                { name: "telephone", label: "Telephone"                   },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
            />
          </div>

          {/* Attendance */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Attendance</p>
            <FormFieldGrid
              fields={[
                { name: "daysAbsent",      label: "Days Absent (Term)", inputMode: "numeric" },
                { name: "totalDaysAbsent", label: "Total Days Absent",  inputMode: "numeric" },
                { name: "timesLate",       label: "Times Late (Term)",  inputMode: "numeric" },
                { name: "totalTimesLate",  label: "Total Times Late",   inputMode: "numeric" },
              ]}
              control={control}
              labelClassName="text-xs font-semibold text-slate-500 uppercase tracking-wide"
              gridClassName="grid grid-cols-2 gap-6"
            />
          </div>
        </div>
      );
    }

    // ── Step 3: Learning Skills (both terms) ──────────────────────────────────
    if (currentStep === 3) {
      const SKILLS = [
        { key: "responsibility",  label: "Responsibility"  },
        { key: "organization",    label: "Organization"    },
        { key: "independentWork", label: "Independent Work"},
        { key: "collaboration",   label: "Collaboration"   },
        { key: "initiative",      label: "Initiative"      },
        { key: "selfRegulation",  label: "Self-Regulation" },
      ];
      return (
        <div className="space-y-5">
          <SectionHeading title="Learning Skills & Work Habits" subtitle="Ratings for both terms" />

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            {/* Column headers */}
            <div className="grid items-center border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-teal-600 text-center">Term 1</span>
              <span />
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 text-center">Term 2</span>
            </div>

            {/* Skill rows */}
            {SKILLS.map(({ key, label }, i) => (
              <div
                key={key}
                className={`grid items-center px-5 py-3 ${i < SKILLS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "160px 1fr 1px 1fr" }}
              >
                <span className="text-sm font-medium text-slate-700">{label}</span>

                {/* Term 1 */}
                <FormField
                  control={control}
                  name={`${key}1`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} colorMap={LSWH_COLOR_MAP} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Divider */}
                <div className="h-8 bg-slate-200" />

                {/* Term 2 */}
                <FormField
                  control={control}
                  name={`${key}2`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector size="sm" value={field.value} onChange={field.onChange} options={selectItems} colorMap={LSWH_COLOR_MAP} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            ))}
          </div>

          {/* LSWH comment */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <AIGeneratedTextarea
              control={control}
              name="strengthsNextStepsLSWH"
              label="Strengths & Next Steps - Learning Skills"
              isGenerating={isGeneratingAI.lswh}
              onGenerate={handleGenerateAI}
              placeholder="Teacher comments on learning skills…"
              labelClassName="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2"
              textareaClassName="min-h-[100px] resize-none text-sm"
              buttonClassName="border-teal-200 text-teal-700 hover:bg-teal-50"
            />
            {aiError && <p className="text-sm text-red-600 mt-2">{aiError}</p>}
          </div>
        </div>
      );
    }

    // ── Step 4: Language ──────────────────────────────────────────────────────
    if (currentStep === 4) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Language" />
          <SubjectCard
            title="Language"
            planField="languagePlan"
            grade1Field="languageGrade1"
            grade2Field="languageGrade2"
            commentsField="languageComments"
            plans={twoPlans}
            control={control}
          />
          <SubjectCard
            title="Native Language"
            nameField="nativeLanguage"
            planField="nativeLanguagePlan"
            grade1Field="nativeLanguageGrade1"
            grade2Field="nativeLanguageGrade2"
            commentsField="nativeLanguageComments"
            plans={twoPlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 5: French ────────────────────────────────────────────────────────
    if (currentStep === 5) {
      const FRENCH_SKILLS = [
        { label: "Listening", planField: "frenchListeningPlan", g1: "frenchListeningGrade1", g2: "frenchListeningGrade2" },
        { label: "Speaking",  planField: "frenchSpeakingPlan",  g1: "frenchSpeakingGrade1",  g2: "frenchSpeakingGrade2"  },
        { label: "Reading",   planField: "frenchReadingPlan",   g1: "frenchReadingGrade1",   g2: "frenchReadingGrade2"   },
        { label: "Writing",   planField: "frenchWritingPlan",   g1: "frenchWritingGrade1",   g2: "frenchWritingGrade2"   },
      ];
      return (
        <div className="space-y-4">
          <SectionHeading title="French" />

          {/* Program type */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">French Program Type</p>
            <FormField
              control={control}
              name="frenchPlan"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={frenchPlans} colorMap={{}} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {/* Four skill rows */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "100px 1fr auto" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Skill</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Plan (ESL/IEP)</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">T1 / T2</span>
            </div>
            {FRENCH_SKILLS.map(({ label, planField, g1, g2 }, i) => (
              <div
                key={planField}
                className={`grid items-center gap-4 px-5 py-3 ${i < FRENCH_SKILLS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "100px 1fr auto" }}
              >
                <span className="text-sm font-semibold text-slate-700">{label}</span>
                <FormField
                  control={control}
                  name={planField}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PillSelector size="sm" value={field.value} onChange={field.onChange} options={twoPlans} colorMap={{}} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <div className="flex items-center gap-2">
                  <FormField
                    control={control}
                    name={g1}
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-center">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                        <FormControl>
                          <input
                            {...field}
                            maxLength={2}
                            className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={control}
                    name={g2}
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-center">
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                        <FormControl>
                          <input
                            {...field}
                            maxLength={2}
                            className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* French comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField
              control={control}
              name="frenchComments"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Comments</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="French program comments…" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      );
    }

    // ── Step 6: Mathematics ───────────────────────────────────────────────────
    if (currentStep === 6) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Mathematics" />
          <SubjectCard
            title="Mathematics"
            planField="mathematicsPlan"
            grade1Field="mathematicsGrade1"
            grade2Field="mathematicsGrade2"
            commentsField="mathematicsComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 7: Science & Technology ─────────────────────────────────────────
    if (currentStep === 7) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Science & Technology" />
          <SubjectCard
            title="Science & Technology"
            planField="scienceAndTechnologyPlan"
            grade1Field="scienceAndTechnologyGrade1"
            grade2Field="scienceAndTechnologyGrade2"
            commentsField="scienceAndTechnologyComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 8: Social Studies ────────────────────────────────────────────────
    if (currentStep === 8) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Social Studies" />
          <SubjectCard
            title="Social Studies"
            planField="socialStudiesPlan"
            grade1Field="socialStudiesGrade1"
            grade2Field="socialStudiesGrade2"
            commentsField="socialStudiesComments"
            plans={threePlans}
            control={control}
          />
        </div>
      );
    }

    // ── Step 9: Health & PE ───────────────────────────────────────────────────
    if (currentStep === 9) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Health & Physical Education" />

          {/* Health Education */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Health Education</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
                <FormField control={control} name="healthEducationPlan" render={({ field }) => (
                  <FormItem><FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} colorMap={{}} />
                  </FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades</p>
                <GradePair control={control} field1="healthEducationGrade1" field2="healthEducationGrade2" />
              </div>
            </div>
          </div>

          {/* Physical Education */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Physical Education</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Program Plan</p>
                <FormField control={control} name="physicalEducationPlan" render={({ field }) => (
                  <FormItem><FormControl>
                    <PillSelector size="sm" value={field.value} onChange={field.onChange} options={threePlans} colorMap={{}} />
                  </FormControl></FormItem>
                )} />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">Term Grades</p>
                <GradePair control={control} field1="physicalEducationGrade1" field2="physicalEducationGrade2" />
              </div>
            </div>
          </div>

          {/* Shared H&PE Comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="healthAndPhysicalEducationComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">
                  H&amp;PE Comments
                </FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Health & Physical Education comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>
        </div>
      );
    }

    // ── Step 10: Arts & Optional Studies ─────────────────────────────────────
    if (currentStep === 10) {
      return (
        <div className="space-y-4">
          <SectionHeading title="Arts" />

          {/* Arts header field */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="artsHeader" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Arts Header</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="e.g. The Arts" className="max-w-xs" />
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Arts subjects - compact table */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="grid border-b border-slate-100 bg-slate-50 px-5 py-2.5"
              style={{ gridTemplateColumns: "1fr 1fr auto" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Subject / Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Program Plan</span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">T1 / T2</span>
            </div>
            {ARTS_ROWS.map(({ nameField, planField, grade1Field, grade2Field, placeholder }, i) => (
              <div
                key={nameField}
                className={`grid items-center gap-4 px-5 py-3 ${i < ARTS_ROWS.length - 1 ? "border-b border-slate-100" : ""}`}
                style={{ gridTemplateColumns: "1fr 1fr auto" }}
              >
                <FormField control={control} name={nameField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <input
                        {...field}
                        placeholder={placeholder}
                        className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-teal-400 focus:outline-none"
                      />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name={planField} render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-center gap-2">
                  <FormField control={control} name={grade1Field} render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={control} name={grade2Field} render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              </div>
            ))}
          </div>

          {/* Arts comments */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5">
            <FormField control={control} name="artsComments" render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-2">Arts Comments</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[90px] resize-none text-sm" placeholder="Arts comments…" />
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Optional Studies */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Optional Studies</span>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr auto" }}>
                <FormField control={control} name="optionalStudies" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Subject Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Islamic Studies" />
                    </FormControl>
                  </FormItem>
                )} />
                <FormField control={control} name="optionalStudiesPlan" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Program Plan</FormLabel>
                    <FormControl>
                      <PillSelector size="sm" value={field.value} onChange={field.onChange} options={fourPlans} colorMap={{}} />
                    </FormControl>
                  </FormItem>
                )} />
                <div className="flex items-end gap-2 pb-0.5">
                  <FormField control={control} name="optionalStudiesGrade1" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T1</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                  <FormField control={control} name="optionalStudiesGrade2" render={({ field }) => (
                    <FormItem className="flex flex-col items-center">
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">T2</span>
                      <FormControl>
                        <input {...field} maxLength={2}
                          className="w-11 h-9 text-center text-base font-bold border-2 border-slate-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none transition-colors"
                        />
                      </FormControl>
                    </FormItem>
                  )} />
                </div>
              </div>
              <FormField control={control} name="optionalStudiesComments" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Comments</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[80px] resize-none text-sm" />
                  </FormControl>
                </FormItem>
              )} />
            </div>
          </div>
        </div>
      );
    }

    // ── Step 11: ERS ──────────────────────────────────────────────────────────
    if (currentStep === 11) {
      return (
        <div className="space-y-5">
          <SectionHeading
            title="Early Reading Strategy"
            subtitle="Record ERS assessment completion and benchmark status"
          />

          {/* Completed */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assessment Completed</p>
            <FormField control={control} name="ersCompleted" render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex gap-3">
                    {ersCompletedOpts.map((opt) => {
                      const isClear = opt.value === "";
                      const isSelected = opt.value === field.value;
                      return (
                        <button
                          key={opt.value + opt.label}
                          type="button"
                          onClick={() => field.onChange(opt.value)}
                          className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-colors cursor-pointer ${
                            isSelected
                              ? isClear
                                ? "bg-slate-600 border-slate-600 text-white"
                                : opt.value === "Yes"
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : opt.value === "No"
                                    ? "bg-red-500 border-red-500 text-white"
                                    : "bg-slate-500 border-slate-500 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {isClear ? "×" : opt.label}
                        </button>
                      );
                    })}
                  </div>
                </FormControl>
              </FormItem>
            )} />
          </div>

          {/* Date */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Assessment Date</p>
            <div className="flex items-end gap-3">
              <FormField control={control} name="ersDateYear" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Year</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="YYYY" maxLength={4} inputMode="numeric"
                      className="w-20 h-10 text-center font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                    />
                  </FormControl>
                </FormItem>
              )} />
              <span className="text-slate-300 text-2xl font-light pb-1">/</span>
              <FormField control={control} name="ersDateMonth" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Month</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="MM" maxLength={2} inputMode="numeric"
                      className="w-14 h-10 text-center font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                    />
                  </FormControl>
                </FormItem>
              )} />
              <span className="text-slate-300 text-2xl font-light pb-1">/</span>
              <FormField control={control} name="ersDateDay" render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Day</FormLabel>
                  <FormControl>
                    <input {...field} placeholder="DD" maxLength={2} inputMode="numeric"
                      className="w-14 h-10 text-center font-bold border-2 border-slate-200 rounded-xl bg-white focus:border-teal-400 focus:outline-none transition-colors"
                    />
                  </FormControl>
                </FormItem>
              )} />
            </div>
          </div>

          {/* Benchmark */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Benchmark Met</p>
            <FormField control={control} name="ersBenchmark" render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex gap-3">
                    {ersBenchmarkOpts.map((opt) => {
                      const isClear = opt.value === "";
                      const isSelected = opt.value === field.value;
                      return (
                        <button
                          key={opt.value + opt.label}
                          type="button"
                          onClick={() => field.onChange(opt.value)}
                          className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-colors cursor-pointer ${
                            isSelected
                              ? isClear
                                ? "bg-slate-600 border-slate-600 text-white"
                                : opt.value === "Yes"
                                  ? "bg-emerald-500 border-emerald-500 text-white"
                                  : opt.value === "No"
                                    ? "bg-red-500 border-red-500 text-white"
                                    : "bg-slate-500 border-slate-500 text-white"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                        >
                          {isClear ? "×" : opt.label}
                        </button>
                      );
                    })}
                  </div>
                </FormControl>
              </FormItem>
            )} />
          </div>
        </div>
      );
    }

    return null;
  };

  const isShared = backUrl?.startsWith("/shared-classes");

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white shrink-0">
        <Link to={backUrl || "/"}>
          <Button variant="ghost" size="sm">
            <LucideArrowLeft size={16} className="mr-1.5" />
            Back
          </Button>
        </Link>
        <div className="text-center">
          <h1
            className="text-lg font-semibold text-slate-800"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Ontario Provincial Report Card
          </h1>
          {contextInfo && (
            <p className="text-xs text-slate-500 leading-none mt-0.5">
              {[contextInfo.grade && `Grade ${contextInfo.grade}`, contextInfo.schoolYear, contextInfo.oen && `OEN ${contextInfo.oen}`, contextInfo.studentName]
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {defaultsGroupId && (
            <Button size="sm" onClick={() => onRequestSave?.(form.getValues())} className="bg-blue-600 hover:bg-blue-700 text-white border-blue-600">
              Save Defaults
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} className="gap-1.5">
            {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
            <span className="text-xs">{showPreview ? "Hide PDF" : "Show PDF"}</span>
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
            {isPdfGenerating ? <span className="text-xs">Updating…</span> : <LucideDownload size={16} />}
          </Button>
        </div>
      </div>

      {/* Shared-class banner */}
      {isShared && (
        <div className="flex items-center gap-2 px-5 py-1.5 bg-amber-50 border-b border-amber-200 text-xs text-amber-700 shrink-0">
          <LucideUsers size={12} className="shrink-0" />
          Shared class - changes you save are visible to the owner
        </div>
      )}

      {/* Class Defaults banner */}
      {defaultsGroupId && (
        <div className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 border-b-2 border-blue-200 shrink-0">
          <LucideSettings2 size={15} className="shrink-0 text-blue-500" />
          <span className="text-sm font-medium text-blue-800">
            Class Defaults — <span className="font-normal">You&apos;re setting default values for all reports in this class. No individual student is being edited.</span>
          </span>
        </div>
      )}

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Form panel */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 ${showPreview ? "w-[55%]" : "w-full"}`}>
          {/* Step indicator */}
          <div className="px-5 pt-4 pb-2 shrink-0 border-b border-slate-100 bg-white">
            <StepIndicator
              currentStep={currentStep}
              completedSteps={completedSteps}
              onStepClick={setCurrentStep}
            />
          </div>

          {/* Free-trial preset toolbar */}
          {!reportId && !defaultsGroupId && (
            <div className="px-5 py-2 shrink-0 flex flex-wrap gap-2 border-b border-slate-100 bg-slate-50">
              {PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-xs transition-all duration-150 hover:scale-105 hover:shadow-md active:scale-95"
                  onClick={() => form.reset(preset.data)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}

          {/* Step content */}
          <div ref={formPaneRef} className="flex-1 overflow-y-auto px-5 py-5 bg-slate-50">
            <Form {...form}>
              <form>{renderStep()}</form>
            </Form>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button type="button" variant="outline" onClick={handlePrev} disabled={currentStep === 1}>
              <LucideChevronLeft size={16} className="mr-1" />
              Prev
            </Button>
            <div className="flex items-center gap-2">
              {!isShared && (
                <Button
                  type="button"
                  size="sm"
                  variant={completedSteps.has(currentStep) ? "default" : "outline"}
                  onClick={() => toggleManualComplete(currentStep)}
                  className={completedSteps.has(currentStep) ? "gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600" : "gap-1.5"}
                >
                  <LucideCheck size={14} />
                  {completedSteps.has(currentStep) ? "Marked Complete" : "Mark Complete"}
                </Button>
              )}
              {currentStep < STEPS.length ? (
                <Button type="button" onClick={handleNext}>
                  Next
                  <LucideChevronRight size={16} className="ml-1" />
                </Button>
              ) : (
                <Button type="button" onClick={handleDownload} disabled={!filledPDF || isPdfGenerating}>
                  <LucideDownload size={16} className="mr-1.5" />
                  {isPdfGenerating ? "Updating…" : "Download PDF"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* PDF preview panel */}
        <div className={`border-l border-slate-200 transition-all duration-300 overflow-hidden ${showPreview ? "w-[45%]" : "w-0"}`}>
          {showPreview && (
            <PDFPreview src={filledPDF} scrollTarget={scrollTargets[currentStep]} defaultPageCount={4} />
          )}
        </div>
      </div>
    </div>
  );
};
